import React, { useMemo, useState } from 'react';
import { Box, Button, IconButton, MenuItem, Select, Stack, Tooltip, Typography } from '@mui/material';
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { addDays, formatDateISO, getMonday } from '../../utils/weekUtils';
import {
  bezettingUitWeekChecks, bouwKalenderRijen, dagenVenster, personenUitWeekChecks, wekenVanVenster,
} from '../../utils/draaidagKalender';
import { PLANNINGSTATUS_STIJL } from './planningstatusStijl';

const FONT = "'Manrope', 'Inter', sans-serif";
const NAAM_BREEDTE = 190;
const RIJ_HOOGTE = 26;

/** Achtergrondtint per bezettingstoestand (week-check): rood = over de regels, amber = vol/druk. */
const BEZETTING_TINT = {
  over: { bg: '#fee2e2', fg: '#b91c1c', tekst: 'over de planningsregels' },
  druk: { bg: '#fef3c7', fg: '#b45309', tekst: 'agenda zit vol' },
  op_max: { bg: '#fef3c7', fg: '#b45309', tekst: 'op het maximum' },
  ruimte: { bg: 'transparent', fg: '#166534', tekst: 'nog ruimte' },
};

/**
 * Kalender/lanes-weergave van het draaidag-overzicht: cameramensen en regisseurs
 * onder elkaar, dagen naast elkaar, draaidagen als blokken in de kleur van hun
 * planningstatus. Per week kleurt de rij mee met de bezetting uit de week-check.
 */
export default function DraaidagenKalender({ rows, weekChecks, vandaagISO, filterActief, onKies, onWeekKlik }) {
  const [startMaandag, setStartMaandag] = useState(() => formatDateISO(getMonday(new Date(`${vandaagISO}T12:00:00`))));
  const [weken, setWeken] = useState(() => Number(localStorage.getItem('draaidagen-kalender-weken')) || 6);

  const dagen = useMemo(() => dagenVenster(startMaandag, weken), [startMaandag, weken]);
  const weekKoppen = useMemo(() => wekenVanVenster(dagen), [dagen]);
  const personen = useMemo(() => personenUitWeekChecks(weekChecks), [weekChecks]);
  const bezetting = useMemo(() => bezettingUitWeekChecks(weekChecks), [weekChecks]);
  const { secties, zonderCrew } = useMemo(
    () => bouwKalenderRijen(rows, personen, dagen, { alleenMetBlokken: filterActief }),
    [rows, personen, dagen, filterActief],
  );

  const schuif = (n) => setStartMaandag(formatDateISO(addDays(new Date(`${startMaandag}T12:00:00`), n * 7)));
  const kiesWeken = (n) => { setWeken(n); try { localStorage.setItem('draaidagen-kalender-weken', String(n)); } catch { /* privémodus */ } };
  const kolommen = `${NAAM_BREEDTE}px repeat(${dagen.length}, minmax(30px, 1fr))`;
  // Tellen op id: één draaidag met drie crewleden is drie blokken maar één
  // draaidag (Codex, 11 sep).
  const aantalBlokken = new Set([
    ...secties.flatMap((sec) => sec.rijen.flatMap((r) => r.blokken.map((b) => b.id))),
    ...zonderCrew.blokken.map((b) => b.id),
  ]).size;

  const Rij = ({ rij, kop }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: kolommen, gridAutoRows: `${RIJ_HOOGTE}px`,
      borderBottom: '1px solid #f1f5f9', '&:hover': { bgcolor: '#fcfcfd' } }}>
      <Box sx={{ gridColumn: 1, gridRow: `1 / span ${rij.lanes}`, position: 'sticky', left: 0, zIndex: 2, bgcolor: '#fff',
        display: 'flex', alignItems: 'center', px: 1.25, borderRight: '1px solid #e2e8f0', minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 12.5, fontWeight: kop ? 800 : 600, color: kop ? '#64748b' : '#0f172a', fontFamily: FONT }}>
          {rij.naam}
        </Typography>
        {rij.blokken.length > 0 && (
          <Typography sx={{ ml: 'auto', pl: 1, fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>{rij.blokken.length}</Typography>
        )}
      </Box>
      {dagen.map((d, i) => {
        const bz = rij.rol ? bezetting[`${d.weekKey}|${rij.naam}`] : null;
        const tint = bz ? BEZETTING_TINT[bz.toestand] : null;
        return (
          <Box key={d.iso} sx={{ gridColumn: i + 2, gridRow: `1 / span ${rij.lanes}`,
            bgcolor: d.weekend ? '#f8fafc' : (tint?.bg || 'transparent'),
            borderLeft: d.eersteVanWeek ? '2px solid #e2e8f0' : '1px solid #f1f5f9',
            ...(d.iso === vandaagISO && { boxShadow: 'inset 0 0 0 1.5px #2563eb' }),
            position: 'relative' }}>
            {d.eersteVanWeek && bz && bz.max > 0 && (
              <Tooltip title={`Week ${d.weekNr}: ${bz.draaidagen} van max ${bz.max} draaidagen — ${tint.tekst}`} arrow>
                <Typography sx={{ position: 'absolute', top: 1, left: 3, fontSize: 9.5, fontWeight: 800, color: tint.fg, lineHeight: 1, cursor: 'default' }}>
                  {bz.draaidagen}/{bz.max}
                </Typography>
              </Tooltip>
            )}
          </Box>
        );
      })}
      {rij.blokken.map((b) => {
        const stijl = PLANNINGSTATUS_STIJL[b.planningstatus] || { bg: '#e0e7ff', fg: '#3730a3' };
        const label = [b.projectnummer, b.titel].filter(Boolean).join(' · ');
        return (
          <Tooltip key={b.id} arrow title={(
            <Box sx={{ fontSize: 12 }}>
              <b>{b.titel}</b>{b.opdrachtgever ? ` — ${b.opdrachtgever}` : ''}<br />
              {b.startDatum}{b.eindDatum && b.eindDatum !== b.startDatum ? ` t/m ${b.eindDatum}` : ''}
              {b.pl ? ` · PL ${b.pl}` : ''}<br />
              Crew: {(b.crew || []).join(', ') || '—'}<br />
              {b.planningstatus || 'Geen planningstatus'}
            </Box>
          )}>
            <Box role="button" tabIndex={0} onClick={() => onKies?.(b.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onKies?.(b.id); } }}
              sx={{ gridColumn: `${b.kolom + 2} / span ${b.breedte}`, gridRow: b.lane + 1, zIndex: 1, m: '3px 2px',
                bgcolor: stijl.bg, color: stijl.fg, borderRadius: b.afgekaptVoor || b.afgekaptNa ? '2px' : '6px',
                px: 0.75, fontSize: 11, fontWeight: 700, fontFamily: FONT, lineHeight: `${RIJ_HOOGTE - 6}px`,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer',
                border: `1px solid ${stijl.fg}22`, '&:hover': { filter: 'brightness(0.96)' },
                '&:focus-visible': { outline: '2px solid #2563eb', outlineOffset: 1 } }}>
              {b.dot ? `${b.dot} ` : ''}{label || 'Draaidag'}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
        <IconButton size="small" onClick={() => schuif(-1)} aria-label="Week terug"><ChevronLeftIcon /></IconButton>
        <Button size="small" variant="text" sx={{ textTransform: 'none', fontWeight: 700 }}
          onClick={() => setStartMaandag(formatDateISO(getMonday(new Date(`${vandaagISO}T12:00:00`))))}>
          Vandaag
        </Button>
        <IconButton size="small" onClick={() => schuif(1)} aria-label="Week vooruit"><ChevronRightIcon /></IconButton>
        <Typography sx={{ fontWeight: 800, fontFamily: FONT, fontSize: 14, color: '#0f172a' }}>
          Week {weekKoppen[0]?.weekNr} – {weekKoppen[weekKoppen.length - 1]?.weekNr}
        </Typography>
        <Select size="small" value={weken} onChange={(e) => kiesWeken(Number(e.target.value))}
          sx={{ fontSize: '0.82rem', bgcolor: '#fff', ml: 1 }}>
          {[4, 6, 8, 12].map((n) => <MenuItem key={n} value={n} sx={{ fontSize: '0.82rem' }}>{n} weken</MenuItem>)}
        </Select>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
          rij-tint = bezetting uit de week-check · blokkleur = planningstatus · klik op een blok voor het detail
        </Typography>
      </Stack>

      <Box sx={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff' }}>
        <Box sx={{ minWidth: NAAM_BREEDTE + dagen.length * 30 }}>
          {/* Weekkoppen */}
          <Box sx={{ display: 'grid', gridTemplateColumns: kolommen, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
            <Box sx={{ gridColumn: 1, position: 'sticky', left: 0, zIndex: 3, bgcolor: '#f8fafc', borderRight: '1px solid #e2e8f0' }} />
            {weekKoppen.map((w) => {
              const wc = weekChecks?.[w.weekKey];
              return (
                <Box key={w.weekKey} sx={{ gridColumn: `${w.kolom + 2} / span ${w.breedte}`, px: 1, py: 0.5,
                  borderLeft: '2px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, fontFamily: FONT, color: '#334155' }}>Week {w.weekNr}</Typography>
                  {wc && (
                    <Tooltip title="Week-check: klik voor wie erover zit en wie nog ruimte heeft" arrow>
                      <Box component="span" onClick={(e) => onWeekKlik?.(e.currentTarget, w.weekKey)}
                        sx={{ cursor: 'pointer', fontSize: 13, lineHeight: 1 }}>
                        {{ ok: '✅', let_op: '⚠️', over: '⛔' }[wc.status] || '✅'}
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Box>
          {/* Dagkoppen */}
          <Box sx={{ display: 'grid', gridTemplateColumns: kolommen, borderBottom: '2px solid #e2e8f0', bgcolor: '#fff' }}>
            <Box sx={{ gridColumn: 1, position: 'sticky', left: 0, zIndex: 3, bgcolor: '#fff', borderRight: '1px solid #e2e8f0',
              display: 'flex', alignItems: 'center', px: 1.25 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {aantalBlokken} draaidag{aantalBlokken === 1 ? '' : 'en'} in beeld
              </Typography>
            </Box>
            {dagen.map((d, i) => (
              <Box key={d.iso} sx={{ gridColumn: i + 2, textAlign: 'center', py: 0.5,
                bgcolor: d.weekend ? '#f8fafc' : 'transparent',
                borderLeft: d.eersteVanWeek ? '2px solid #e2e8f0' : '1px solid #f1f5f9',
                ...(d.iso === vandaagISO && { bgcolor: '#dbeafe' }) }}>
                <Typography sx={{ fontSize: 10, color: '#94a3b8', lineHeight: 1.1, fontWeight: 700 }}>{d.weekdag}</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: d.iso === vandaagISO ? '#1d4ed8' : '#334155', lineHeight: 1.2 }}>{d.dagNr}</Typography>
              </Box>
            ))}
          </Box>

          {secties.length === 0 && zonderCrew.blokken.length === 0 && (
            <Typography sx={{ p: 3, color: '#94a3b8', fontSize: 13 }}>
              Geen draaidagen in deze weken{filterActief ? ' binnen de filters' : ''}.
            </Typography>
          )}
          {secties.map((sec) => (
            <Box key={sec.key}>
              <Box sx={{ position: 'sticky', left: 0, px: 1.25, py: 0.5, bgcolor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.6, fontFamily: FONT }}>
                  {sec.label} · {sec.rijen.length}
                </Typography>
              </Box>
              {sec.rijen.map((rij) => <Rij key={`${sec.key}|${rij.naam}`} rij={rij} />)}
            </Box>
          ))}
          {zonderCrew.blokken.length > 0 && (
            <Box>
              <Box sx={{ position: 'sticky', left: 0, px: 1.25, py: 0.5, bgcolor: '#fff7ed', borderBottom: '1px solid #fed7aa' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#9a3412', textTransform: 'uppercase', letterSpacing: 0.6, fontFamily: FONT }}>
                  Nog geen cameraman of regisseur · {zonderCrew.blokken.length}
                </Typography>
              </Box>
              <Rij rij={zonderCrew} kop />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
